import { PROMPT_SECTIONS, SECTION_HEADINGS, emptyPromptV2 } from './types';
import type { PromptSectionKey, PromptV2 } from './types';

/** Anam guidance: keep prompts under 8,000 tokens. Warn well before that. */
export const TOKEN_LIMIT = 8000;
export const TOKEN_WARN = 6500;

/**
 * Rough token estimate (~4 chars/token).
 *
 * Deliberately an estimate: the exact count depends on Anam's chosen LLM
 * tokenizer, so precision here would be false precision. `scripts/audit-prompt-tokens.mjs`
 * uses a real tokenizer for reporting; this is for live UI feedback.
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

export function stripHtml(html: string | null | undefined): string {
  return String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Append a non-empty line to a section. Blank/whitespace values are dropped. */
export function push(prompt: PromptV2, key: PromptSectionKey, line: string | null | undefined): void {
  const text = String(line ?? '').trim();
  if (text) prompt.sections[key].push(text);
}

/** Append `label: value` when value is present. */
export function pushField(
  prompt: PromptV2,
  key: PromptSectionKey,
  label: string,
  value: string | null | undefined,
): void {
  const text = stripHtml(value);
  if (text) push(prompt, key, `${label}: ${text}`);
}

/**
 * Compile the five sections into the final system prompt.
 *
 * Emits Anam's canonical section order with markdown headings. V2 requires all
 * five sections: a prompt missing Guardrails or Goal is a conversion failure,
 * not a stylistic choice, so `missingSections()` surfaces it rather than the
 * compiler silently emitting a shorter prompt.
 */
export function compilePrompt(prompt: PromptV2): string {
  const blocks: string[] = [];

  for (const key of PROMPT_SECTIONS) {
    const lines = prompt.sections[key].filter(Boolean);
    if (lines.length === 0) continue;
    blocks.push(`# ${SECTION_HEADINGS[key]}\n${lines.join('\n')}`);
  }

  return blocks.join('\n\n');
}

/** Sections with no content. V2 requires all five to be populated. */
export function missingSections(prompt: PromptV2): PromptSectionKey[] {
  return PROMPT_SECTIONS.filter((key) => prompt.sections[key].filter(Boolean).length === 0);
}

export interface CompileReport {
  prompt: string;
  tokens: number;
  overLimit: boolean;
  warn: boolean;
  sectionTokens: Record<PromptSectionKey, number>;
  /** Empty when the prompt is complete; otherwise the sections still to fill. */
  missing: PromptSectionKey[];
  complete: boolean;
}

export function compileWithReport(prompt: PromptV2): CompileReport {
  const text = compilePrompt(prompt);
  const tokens = estimateTokens(text);
  const sectionTokens = {} as Record<PromptSectionKey, number>;

  for (const key of PROMPT_SECTIONS) {
    sectionTokens[key] = estimateTokens(prompt.sections[key].join('\n'));
  }

  const missing = missingSections(prompt);

  return {
    prompt: text,
    tokens,
    overLimit: tokens > TOKEN_LIMIT,
    warn: tokens > TOKEN_WARN && tokens <= TOKEN_LIMIT,
    sectionTokens,
    missing,
    complete: missing.length === 0,
  };
}

export { emptyPromptV2 };
